import React, { useCallback, useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import PolicyManagementTable from "./PolicyManagementTable";
import { MimshakDataUtil } from "../../../services/mimshakDataUtil";
import { MimshakService } from "../../../services/mimshakService";
import GroupByHandlerPieChartWithListInteractive from "../../Story/GroupByHandlerPieChartWithListInteractive";
import GroupByAdjustedProductTypePieChartWithList from "../../Story/GroupByAdjustedProductTypePieChartWithList";
import { MimshakGroupByService } from "../../../services/MimshakGroupByService";
import { formatCurrency } from "../../../utils/namesFormater";
import { formatHandlerCodeUniversal } from "../../../utils/handlerCodeUniversalFormatter";
import { formatProductTypeUniversal } from "../../../utils/productTypeUniversalFormatter";
import { formatRuleType } from "../../../utils/insightsRuleUtils";
import InsightsSummaryByCategory from "../../Insights/InsightsSummaryByCategory";
import {
  usePolicyInsightsByContactIdsAndPeriod,
  usePolicyInsightsByContactIdsPreAdvice,
} from "../../../hooks/insights/usePolicyInsightsRuleBased";
import { RuleType } from "../../../interfaces/DTO/insights/PolicyInsightsRuleBased";
import { ProductType } from "../../../interfaces/DTO/mislaka_xml_models";
import UserTaskMimshakSummary from "../../UserTask/UserTaskMimshakSummary";
import PolicyActionsButton from "../../UserTask/Workflow/PolicyActionsButton";
import { useUserTaskMimshaksByContacts } from "../../../hooks/userTasks/useUserTaskMimshakData";

// בכרטיס הביטוחים מוצגים רק סיכון טהור וביטוח חיים משכנתא
const INSURANCE_CARD_PRODUCT_TYPES: number[] = [ProductType.PURE_RISK_POLICY, ProductType.MORTGAGE_LIFE_INSURANCE];

const getInsuranceCost = (item: MimshakData) => item.deposit_amount || 0;
const countPolicy = () => 1;

interface MimshakManagementPageProps {
  data: MimshakData[];
  viewedType: "USER" | "FAMILY";
  selectedPeriodType?: "REGULAR" | "PRE_ADVICE";
  pensionMimshakData?: MimshakData[];
  capitalMimshakData?: MimshakData[];
  insuranceMimshakData?: MimshakData[];
  /** the investment portfolios loaded by hand (ManualMimshakData, INVESTMENT_PORTFOLIO) - with the policies */
  investmentMimshakData?: MimshakData[];
  contactIds?: number[];
  selectedPeriod?: string;
}

const MimshakManagementPage: React.FC<MimshakManagementPageProps> = ({
  data,
  viewedType,
  selectedPeriodType,
  pensionMimshakData = [],
  capitalMimshakData = [],
  insuranceMimshakData = [],
  investmentMimshakData = [],
  contactIds = [],
  selectedPeriod = "",
}) => {
  // the policies and the investment portfolios - one list for the products chart, the table and the actions
  const allData = useMemo(() => [...data, ...investmentMimshakData], [data, investmentMimshakData]);

  const [handlerFilter, setHandlerFilter] = useState<string | null>(null);
  const [productTypeFilter, setProductTypeFilter] = useState<number | null>(null);
  const [ruleTypeFilter, setRuleTypeFilter] = useState<RuleType | null>(null);

  const isPreAdvice = selectedPeriodType === "PRE_ADVICE";
  const { data: periodInsights = [] } = usePolicyInsightsByContactIdsAndPeriod(contactIds, selectedPeriod, !isPreAdvice);
  const { data: preAdviceInsights = [] } = usePolicyInsightsByContactIdsPreAdvice(contactIds, isPreAdvice);
  const allInsights = isPreAdvice ? preAdviceInsights : periodInsights;
  const { data: userTaskMimshaks = [] } = useUserTaskMimshaksByContacts(contactIds);

  // פוליסות (contact_id + policy_number) שיש להן תובנה מסוג התובנה הנבחר
  const ruleTypePolicyKeys = useMemo(
    () =>
      ruleTypeFilter === null
        ? null
        : new Set(
            allInsights
              .filter((insight) => insight.rule_type === ruleTypeFilter)
              .map((insight) => `${insight.contact_id}|${insight.policy_number}`)
          ),
    [allInsights, ruleTypeFilter]
  );

  const filterByRuleType = (items: MimshakData[]) =>
    ruleTypePolicyKeys === null
      ? items
      : items.filter((item) => ruleTypePolicyKeys.has(`${item.contact_id}|${item.policy_number}`));

  const filterByProductType = (items: MimshakData[]) =>
    productTypeFilter === null
      ? items
      : items.filter((item) => MimshakGroupByService.getProductTypeAdjustingPureSavings(item) === productTypeFilter);

  const ruleFilteredData = useMemo(() => filterByRuleType(allData), [allData, ruleTypePolicyKeys]);
  const filteredData = useMemo(() => filterByProductType(ruleFilteredData), [ruleFilteredData, productTypeFilter]);
  const filteredPensionMimshakData = useMemo(
    () => filterByProductType(filterByRuleType(pensionMimshakData)),
    [pensionMimshakData, ruleTypePolicyKeys, productTypeFilter]
  );
  // an investment portfolio is capital - in the capital chart and its total
  const filteredCapitalMimshakData = useMemo(
    () => filterByProductType(filterByRuleType([...capitalMimshakData, ...investmentMimshakData])),
    [capitalMimshakData, investmentMimshakData, ruleTypePolicyKeys, productTypeFilter]
  );

  // ביטוחים — אין צבירה, לכן מקבצים לפי סוג מוצר את העלות (deposit_amount)
  const insuranceFilteredData = useMemo(
    () =>
      filterByRuleType(insuranceMimshakData).filter(
        (item) =>
          INSURANCE_CARD_PRODUCT_TYPES.includes(item.product_type) &&
          (handlerFilter === null || item.handler_code === handlerFilter)
      ),
    [insuranceMimshakData, ruleTypePolicyKeys, handlerFilter]
  );
  // עלות לפי סוג מוצר — לתצוגה בלבד (הפאי מחולק לפי מספר פוליסות)
  const insuranceCostByType = useMemo(
    () => MimshakGroupByService.groupByProductTypeAdjustingPureSavings(insuranceFilteredData, getInsuranceCost),
    [insuranceFilteredData]
  );
  const formatInsuranceRow = useCallback(
    (productType: number) => formatCurrency(insuranceCostByType[productType] || 0),
    [insuranceCostByType]
  );

  const pensionProducts = useMemo(() => MimshakDataUtil.extractAllProducts(filteredPensionMimshakData), [filteredPensionMimshakData]);
  const capitalProducts = useMemo(() => MimshakDataUtil.extractAllProducts(filteredCapitalMimshakData), [filteredCapitalMimshakData]);

  const capitalTotal = useMemo(() => MimshakService.calculateTotalFundSumCapital(capitalProducts), [capitalProducts]);
  const pensionTotal = useMemo(() => MimshakService.calculateTotalFundSumPension(pensionProducts), [pensionProducts]);

  const filteredInsights = useMemo(
    () =>
      allInsights.filter(
        (insight) =>
          (handlerFilter === null || insight.handler_code === handlerFilter) &&
          (productTypeFilter === null ||
            MimshakGroupByService.getProductTypeAdjustingPureSavings(insight) === productTypeFilter)
      ),
    [allInsights, handlerFilter, productTypeFilter]
  );

  const productTypeTotal = useMemo(
    () => Object.values(MimshakGroupByService.groupByProductTypeAdjustingPureSavings(ruleFilteredData)).reduce((sum, v) => sum + v, 0),
    [ruleFilteredData]
  );

  // מה שמוצג בטבלה (כולל פילטר היצרן שמופעל בתוך הטבלה) – לסה"כ ולקופות של כפתור הפעולות
  const tableRows = useMemo(
    () => filteredData.filter((r) => handlerFilter === null || r.handler_code === handlerFilter),
    [filteredData, handlerFilter]
  );
  const tableTotal = useMemo(() => tableRows.reduce((sum, r) => sum + (r.funds_sum || 0), 0), [tableRows]);

  // כותרת הטבלה — מתארת את הפילטרים הפעילים: סוג מוצר · יצרן · סוג תובנה
  const tableTitle = useMemo(() => {
    const parts = [
      productTypeFilter !== null ? formatProductTypeUniversal(productTypeFilter) : null,
      handlerFilter !== null ? formatHandlerCodeUniversal(handlerFilter) : null,
      ruleTypeFilter !== null ? formatRuleType(ruleTypeFilter) : null,
    ].filter(Boolean);
    return parts.length > 0 ? `סה"כ ${parts.join(" · ")}` : 'סה"כ';
  }, [productTypeFilter, handlerFilter, ruleTypeFilter]);

  return (
    <Box dir="rtl">
      {/* שורת מטריקות — פיזור בין יצרנים */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, overflowX: "auto", pb: 1 }}>
        {productTypeTotal > 0 && (
          <Paper sx={{ borderRadius: 3, boxShadow: 2, bgcolor: "#fff", width: 380, flexShrink: 0, py: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, textAlign: "center" }}>
              מוצרים {formatCurrency(productTypeTotal)}
            </Typography>
            <GroupByAdjustedProductTypePieChartWithList
              data={ruleFilteredData}
              selectedProductType={productTypeFilter}
              onProductTypeFilter={setProductTypeFilter}
            />
          </Paper>
        )}
        {capitalProducts.length > 0 && (
          <Paper sx={{ borderRadius: 3, boxShadow: 2, bgcolor: "#fff", width: 300, flexShrink: 0, py: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, textAlign: "center" }}>
              הון {formatCurrency(capitalTotal)}
            </Typography>
            <GroupByHandlerPieChartWithListInteractive
              products={capitalProducts}
              selectedHandler={handlerFilter}
              onHandlerFilter={setHandlerFilter}
            />
          </Paper>
        )}
        {pensionProducts.length > 0 && (
          <Paper sx={{ borderRadius: 3, boxShadow: 2, bgcolor: "#fff", width: 300, flexShrink: 0, py: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, textAlign: "center" }}>
              פנסיה {formatCurrency(pensionTotal)}
            </Typography>
            <GroupByHandlerPieChartWithListInteractive
              products={pensionProducts}
              selectedHandler={handlerFilter}
              onHandlerFilter={setHandlerFilter}
            />
          </Paper>
        )}
        {contactIds.length > 0 && (
          <Paper sx={{ borderRadius: 3, boxShadow: 2, bgcolor: "#fff", width: 300, flexShrink: 0, py: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, textAlign: "center" }}>
              תובנות {filteredInsights.length}
            </Typography>
            <InsightsSummaryByCategory
              insights={filteredInsights}
              selectedRuleType={ruleTypeFilter}
              onRuleTypeFilter={setRuleTypeFilter}
            />
          </Paper>
        )}
        {userTaskMimshaks.length > 0 && (
          <Paper sx={{ borderRadius: 3, boxShadow: 2, bgcolor: "#fff", width: 300, flexShrink: 0, py: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, textAlign: "center" }}>
              פעולות {userTaskMimshaks.length}
            </Typography>
            <UserTaskMimshakSummary tasks={userTaskMimshaks} />
          </Paper>
        )}
        {insuranceFilteredData.length > 0 && (
          <Paper sx={{ borderRadius: 3, boxShadow: 2, bgcolor: "#fff", width: 300, flexShrink: 0, py: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, textAlign: "center" }}>
              ביטוחים {insuranceFilteredData.length} פוליסות
            </Typography>
            <GroupByAdjustedProductTypePieChartWithList
              data={insuranceFilteredData}
              getValue={countPolicy}
              formatValue={formatInsuranceRow}
              selectedProductType={productTypeFilter}
              onProductTypeFilter={setProductTypeFilter}
            />
          </Paper>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          {tableTitle} {formatCurrency(tableTotal)}
        </Typography>
        <PolicyActionsButton policies={tableRows} allPolicies={allData} />
      </Box>

      <PolicyManagementTable
        data={filteredData}
        viewedType={viewedType}
        selectedPeriodType={selectedPeriodType}
        handlerFilter={handlerFilter}
      />
    </Box>
  );
};

export default MimshakManagementPage;
